import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material'
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  EditNote as EditNoteIcon,
  Assignment as AssignmentIcon,
  PersonOff as PersonOffIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { withdrawUser } from '@/services/userService'

interface AppSidebarProps {
  open: boolean
  onClose: () => void
  /** ホーム以外のページで「ホームへ戻る」を表示する */
  showBackToHome?: boolean
  /** ロール切替UIを表示する(ダッシュボード画面のみ) */
  showRoleSwitcher?: boolean
  /**
   * 表示中のルームID。マイプロフィールを開く際に引き継ぐ。
   * これが無いとルームのカテゴリを判定できず、婚活・転職ルームでも
   * 常にビジネス用のプロフィールが表示される。
   */
  roomId?: string
}

export const AppSidebar = ({ open, onClose, showBackToHome = false, showRoleSwitcher = false, roomId }: AppSidebarProps) => {
  const { user, logout, isAdmin, switchRole } = useAuth()
  const navigate = useNavigate()

  const [withdrawalOpen, setWithdrawalOpen] = useState(false)
  const [switchingRole, setSwitchingRole] = useState(false)

  const getInitials = (name: string | null, email: string | null) => {
    if (name) return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    return email?.charAt(0).toUpperCase() || 'U'
  }

  const handleLogout = async () => {
    onClose()
    await logout()
    navigate('/login')
  }

  const handleNavigate = (path: string) => {
    onClose()
    navigate(path)
  }

  const handleWithdrawalClick = () => {
    setWithdrawalOpen(true)
  }

  const handleWithdrawalConfirm = async () => {
    try {
      await withdrawUser()
      setWithdrawalOpen(false)
      onClose()
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('退会処理に失敗しました:', error)
      setWithdrawalOpen(false)
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>メニュー</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ユーザー情報 */}
          {user && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: isAdmin ? 'warning.50' : 'grey.50',
                ...(isAdmin && { border: 1, borderColor: 'warning.200' }),
              }}
            >
              <Avatar sx={{ width: 40, height: 40, bgcolor: isAdmin ? 'warning.main' : undefined }}>
                {isAdmin
                  ? <AdminIcon sx={{ fontSize: 20 }} />
                  : getInitials(user.displayName, user.email)
                }
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {user.displayName || user.email}
                </Typography>
                {isAdmin ? (
                  <Chip label="管理者" size="small" color="warning" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                ) : (
                  <Chip label="参加者" size="small" color="info" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                )}
              </Box>
            </Box>
          )}

          {/* ロール切替(ダッシュボード画面のみ表示) */}
          {user && showRoleSwitcher && (
            <Box sx={{ px: 2, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>表示モード</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  label="管理者"
                  size="small"
                  color={isAdmin ? 'warning' : 'default'}
                  variant={isAdmin ? 'filled' : 'outlined'}
                  disabled={switchingRole}
                  onClick={async () => { if (!isAdmin) { setSwitchingRole(true); try { await switchRole(1) } finally { setSwitchingRole(false) } } }}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
                <Chip
                  label="参加者"
                  size="small"
                  color={!isAdmin ? 'info' : 'default'}
                  variant={!isAdmin ? 'filled' : 'outlined'}
                  disabled={switchingRole}
                  onClick={async () => { if (isAdmin) { setSwitchingRole(true); try { await switchRole(2) } finally { setSwitchingRole(false) } } }}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </Box>
            </Box>
          )}

          {/* ナビゲーション */}
          <List disablePadding sx={{ flex: 1 }}>
            {showBackToHome && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/')}>
                  <ListItemIcon><ArrowBackIcon /></ListItemIcon>
                  <ListItemText primary="ホームへ戻る" />
                </ListItemButton>
              </ListItem>
            )}

            {user?.userId && showBackToHome && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() =>
                    handleNavigate(
                      roomId ? `/profile/${user.userId}?roomId=${roomId}` : `/profile/${user.userId}`,
                    )
                  }
                >
                  <ListItemIcon><AccountCircleIcon /></ListItemIcon>
                  <ListItemText primary="マイプロフィール" />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/profile/edit')}>
                <ListItemIcon><EditNoteIcon /></ListItemIcon>
                <ListItemText primary="プロフィール編集" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/sub-info/edit')}>
                <ListItemIcon><AssignmentIcon /></ListItemIcon>
                <ListItemText primary="サブ情報編集" />
              </ListItemButton>
            </ListItem>

            <Divider sx={{ my: 1 }} />

            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText primary="ログアウト" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={handleWithdrawalClick} sx={{ color: 'error.main' }}>
                <ListItemIcon><PersonOffIcon sx={{ color: 'error.main' }} /></ListItemIcon>
                <ListItemText primary="退会する" />
              </ListItemButton>
            </ListItem>
          </List>

        </Box>
      </Drawer>

      {/* 退会確認モーダル */}
      <Dialog
        open={withdrawalOpen}
        onClose={() => setWithdrawalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOffIcon color="error" />
          退会の確認
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            この操作は取り消せません。
          </Alert>
          <Typography variant="body2" color="text.secondary">
            退会するとアカウントおよび登録済みのすべてのデータが削除されます。本当に退会しますか？
          </Typography>
          {user && (
            <Box sx={{ mt: 2, px: 2, py: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">退会対象アカウント</Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user.displayName || user.email}
              </Typography>
              {user.email && user.displayName && (
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: 'column' }}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            startIcon={<PersonOffIcon />}
            onClick={handleWithdrawalConfirm}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            退会する
          </Button>
          <Button
            variant="text"
            fullWidth
            color="inherit"
            onClick={() => setWithdrawalOpen(false)}
            sx={{ borderRadius: 2, color: 'text.secondary' }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
